<template>
  <b-card header-tag="header">
    <template v-slot:header>
      <h6 class="mb-0" v-if="showName">Camera Configuration ({{ getSafe(() => device.object) }})</h6>
      <h6 class="mb-0" v-else>Camera Configuration</h6>
    </template>

    <!-- Recording -->
    <b-form-group class="mb-3">
      <template v-slot:label>
        <span class="font-weight-bold">Recording</span>
        <small class="text-muted d-block">
          While ON, every frame is saved on the server. While OFF, only the live
          view keeps updating (no history is stored).
        </small>
      </template>
      <b-form-checkbox v-model="recording" switch size="lg" :disabled="sending" @change="onRecordingChange">
        {{ recording ? "Recording…" : "Not recording" }}
      </b-form-checkbox>
    </b-form-group>

    <hr class="my-3" />

    <!-- White LED -->
    <b-form-group class="mb-3">
      <template v-slot:label>
        <span class="font-weight-bold">White LED</span>
        <small class="text-muted d-block">Turn the onboard flash LED on or off.</small>
      </template>
      <b-form-checkbox v-model="led" switch size="lg" :disabled="sending" @change="onLedChange">
        {{ led ? "On" : "Off" }}
      </b-form-checkbox>
    </b-form-group>

    <hr class="my-3" />

    <!-- Interval -->
    <b-form-group class="mb-3">
      <template v-slot:label>
        <span class="font-weight-bold">Frame interval</span>
        <small class="text-muted d-block">How often the camera sends a frame.</small>
      </template>
      <b-form-select v-model="interval" :options="intervalOptions" :disabled="sending" @change="onIntervalChange"></b-form-select>
    </b-form-group>

    <!-- Quality -->
    <b-form-group class="mb-3">
      <template v-slot:label>
        <span class="font-weight-bold">Image quality</span>
        <small class="text-muted d-block">Higher quality means bigger frames.</small>
      </template>
      <b-form-select v-model="quality" :options="qualityOptions" :disabled="sending" @change="onQualityChange"></b-form-select>
    </b-form-group>

    <!-- Size -->
    <b-form-group class="mb-3">
      <template v-slot:label>
        <span class="font-weight-bold">Resolution</span>
        <small class="text-muted d-block">Bigger sizes lower the frame rate.</small>
      </template>
      <b-form-select v-model="size" :options="sizeOptions" :disabled="sending" @change="onSizeChange"></b-form-select>
    </b-form-group>

    <hr class="my-3" />

    <!-- Night mode -->
    <b-form-group class="mb-1">
      <template v-slot:label>
        <span class="font-weight-bold">Night mode</span>
        <small class="text-muted d-block">Tunes the sensor for low-light scenes.</small>
      </template>
      <b-form-checkbox v-model="night" switch size="lg" :disabled="sending" @change="onNightChange">
        {{ night ? "Enabled" : "Disabled" }}
      </b-form-checkbox>
    </b-form-group>
  </b-card>
</template>

<script>
import {
  BROADCAST_ACTION,
  CAMERA_RECORDING_SET,
  CAMERA_RECORDING_STATUS,
} from "@/core/services/store/devices.module";

export default {
  name: "suitch-camera-config-widget",
  props: {
    device: {
      type: Object,
      required: true,
    },
    showName: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sending: false,
      recording: false,
      led: false,
      interval: 5000, // matches firmware default
      intervalOptions: [
        { text: "Near real-time", value: 0 },
        { text: "5 seconds", value: 5000 },
        { text: "10 seconds", value: 10000 },
        { text: "15 seconds", value: 15000 },
        { text: "30 seconds", value: 30000 },
        { text: "1 minute", value: 60000 },
        { text: "5 minutes", value: 300000 },
        { text: "10 minutes", value: 600000 },
      ],
      quality: 16, // esp_camera 0..63, lower = better
      qualityOptions: [
        { text: "High (8)", value: 8 },
        { text: "Medium (16)", value: 16 },
        { text: "Low (32)", value: 32 },
      ],
      size: "320x240",
      sizeOptions: [
        { text: "320 x 240 (QVGA)", value: "320x240" },
        { text: "640 x 480 (VGA)", value: "640x480" },
        { text: "800 x 600 (SVGA)", value: "800x600" },
      ],
      night: false,
    };
  },
  mounted() {
    // best-effort: reflect the current recording session state on load
    this.$store
      .dispatch(CAMERA_RECORDING_STATUS, { token: this.device.token })
      .then((data) => {
        if (data && typeof data.recording !== "undefined") {
          this.recording = !!data.recording;
        }
      })
      .catch(() => {
        /* status is optional; leave the toggle at its default */
      });
  },
  methods: {
    getSafe(fn) {
      try {
        return fn();
      } catch (e) {
        return "";
      }
    },
    onIntervalChange(value) {
      this.sendCommand("interval " + value, "Frame interval updated");
    },
    onQualityChange(value) {
      this.sendCommand("quality " + value, "Image quality updated");
    },
    onSizeChange(value) {
      this.sendCommand("size " + value, "Resolution updated");
    },
    onNightChange(value) {
      this.sendCommand("night " + (value ? 1 : 0), "Night mode " + (value ? "enabled" : "disabled"));
    },
    onLedChange(value) {
      // onboard white LED (GPIO4) — firmware maps "on"/"off" to the LED
      this.sendCommand(value ? "on" : "off", "White LED " + (value ? "on" : "off"));
    },
    onRecordingChange(value) {
      this.sending = true;
      this.$store
        .dispatch(CAMERA_RECORDING_SET, {
          token: this.device.token,
          secret: this.device.secret_token,
          active: value,
        })
        .then(() => {
          this.$bvToast.toast(value ? "Recording started" : "Recording stopped", {
            title: "Camera",
            variant: "success",
            solid: true,
          });
        })
        .catch(() => {
          // revert the toggle if the server rejected it
          this.recording = !value;
          this.$bvToast.toast("Could not change recording state", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.sending = false;
        });
    },
    // interval/quality/size/night go through the existing device command channel
    sendCommand(commandData, okMessage) {
      this.sending = true;
      return this.$store
        .dispatch(BROADCAST_ACTION, {
          token: this.device.token,
          secret: this.device.secret_token,
          command_data: commandData,
        })
        .then(() => {
          this.$bvToast.toast(okMessage, {
            title: "Camera Configuration",
            variant: "success",
            solid: true,
          });
        })
        .catch((error) => {
          this.$bvToast.toast(`Failed to send "${commandData}" to the camera`, {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          throw error;
        })
        .finally(() => {
          this.sending = false;
        });
    },
  },
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}
</style>
